import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

export const DELETE_FOREVER_PHRASE = 'delete forever';

export const confirmPhraseSx = {
  fontFamily: 'Courier, monospace',
  fontWeight: 600,
} as const;

export const ConfirmPhraseText = () => (
  <Box component="strong" sx={confirmPhraseSx}>
    {DELETE_FOREVER_PHRASE}
  </Box>
);

type DeleteForeverDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

const DeleteForeverDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  onClose,
  onConfirm,
}: DeleteForeverDialogProps) => {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const matched = phrase === DELETE_FOREVER_PHRASE;

  const handleClose = () => {
    if (busy) return;
    setPhrase('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!matched || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      setPhrase('');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {message}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Type
          {' '}
          <ConfirmPhraseText />
          {' '}
          to confirm:
        </Typography>
        <TextField
          fullWidth
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          autoFocus
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!matched || busy}
          onClick={() => { handleConfirm().catch(() => {}); }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteForeverDialog;
